"use strict";

import AppUtil from "/js/util/app-util.js";
import FadeUtil from "/js/util/fade-util.js";
import CssUtil from "/js/util/css-util.js";
import Global from "/js/core/global.js";
import ServiceUtil from "/js/util/service-util.js";
import UserLessons from "/js/core/user-lessons.js";

/**
 * Helper class for App to transition between the various top-level views.
 * Is tightly coupled with `App`.
 */
export default class AppTransitionUtil {

  #app;

  constructor(app) {
    this.#app = app;
  }

  /** Shows lesson view after startup. */
  startupToLesson(lessonId) {
    Global.lessonId = lessonId;
    this.#app.loadLessonAndCallback(() => {
      AppUtil.updateBodyClasses("lesson");
      this.#app.lessonView.startLesson(false);
    });
  }

  /** Shows minicourse view after startup. */
  startupToMinicourse() {
    AppUtil.updateBodyClasses("minicourse");
    this.#app.minicourseView.show();
  }

  /** Transitions from minicourse view to lesson view. */
  minicourseToLesson(lessonId) {
    this.#app.minicourseView.hide();
    Global.lessonId = lessonId;  // TODO: revisit this
    this.#app.loadLessonAndCallback(() => {  // TODO: block during loading
      AppUtil.updateBodyClasses("lesson");
      this.#app.minicourseView.showAsClosedSidebar();
      this.#app.lessonView.startLesson(false);
    });
  }

  /** Simply transitions back to the preexisting lesson in its current state. */
  minicourseToPreexistingLesson() {
    this.#app.minicourseView.showAsClosedSidebar();
    this.#app.lessonView.reshow();
  }

  /** Transitions from minicourse view to recap view. Asynchronous. */
  minicourseToRecap(lessonId) {
    this.#app.minicourseView.hide();
    Global.lessonId = lessonId;
    this.#app.loadLessonAndCallback(() => {
      AppUtil.updateBodyClasses("recap");
      this.#app.minicourseView.showAsClosedSidebar();
      this.#app.recapView.show(true);
    });
  }

  /** Transitions from any of the main full-screen views back to minicourse view. */
  anyToMinicourse() {
    if (Global.isLiveMode) {  // TODO: is wrong place now
      ServiceUtil.updateUserLesson(UserLessons.current);
    }
    AppUtil.setCssWithoutTransition(this.#app.minicourseView.$el, () => {
      this.#app.minicourseView.$el.removeClass("isSidebar fadeToHidden fadeFromHidden");
      this.#app.minicourseView.$el.addClass("fadeToHidden");  // don't ask
    });

    this.#app.lessonView.hide();
    this.#app.doneView.hide();
    this.#app.recapView.hide();

    // TODO: Currently cross-fades, which looks bad. Needs a delay here.
    AppUtil.updateBodyClasses("minicourse");
    this.#app.minicourseView.show();
  }

  /** Transitions from lesson view to done view. */
  lessonToDone() {
    this.#app.lessonView.hide(true);
    AppUtil.updateBodyClasses("done");
    this.#app.doneView.show();
  }

  /** Transitions from done view to recap view. */
  doneToRecap() {
    this.#app.doneView.hide();
    AppUtil.updateBodyClasses("recap");
    this.#app.recapView.show();
  }

  /** Transitions from recap view to same lesson, starting at the beginning. */
  recapToReplay() {
    this.#app.recapView.hide();
    AppUtil.updateBodyClasses("lesson");
    this.#app.lessonView.startLesson(true);
  }

  /**
   * Transitions from either lesson view, done view, or recap view
   * to a different lesson or recap view.
   *
   * @param lessonId
   * @param alwaysFromStart will always show lesson from first card regardless of previous progress.
   */
  lessonDoneOrRecapToDifferentLessonOrRecap(lessonId, alwaysFromStart=false) {

    if (Global.isLiveMode) {
      ServiceUtil.updateUserLesson(UserLessons.current);
    }

    const part2 = () => {
      Global.lessonId = lessonId;  // TODO: is this necessary??
      this.#app.loadLessonAndCallback(() => {
        if (!alwaysFromStart && UserLessons.isComplete(lessonId)) {
          AppUtil.updateBodyClasses("recap");
          this.#app.recapView.show(true);
        } else {
          AppUtil.updateBodyClasses("lesson");
          this.#app.lessonView.startLesson(alwaysFromStart);
        }
      });
    };

    // Fade out currently visible view
    // TODO: should use native hide() functions that can call back
    let $el;
    if (CssUtil.isVisible(this.#app.lessonView.$el)) {
      $el = this.#app.lessonView.$el;
    } else if (CssUtil.isVisible(this.#app.doneView.$el)) {
      $el = this.#app.doneView.$el;
    } else if (CssUtil.isVisible(this.#app.recapView.$el)) {
      $el = this.#app.recapView.$el;
    } else {
      cl('logic error?');
      return;
    }
    FadeUtil.fadeOut($el, part2);
    // TODO: should use view's show/hide functions?
    // TODO: Need to block
  }
}
